package org.gymly.ui.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CheckboxDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.launch
import org.gymly.data.AdminRepo
import org.gymly.model.AdminPaypalMode
import org.gymly.model.AdminSettingsPaymentUpsert
import org.gymly.theme.AdminColors
import org.gymly.ui.components.PasswordVisibilityToggle
import org.gymly.ui.components.VerticalScrollColumn
import org.gymly.ui.components.rememberPasswordVisible
import org.gymly.util.userMessage

@Composable
internal fun SettingsPaymentsPanel(compact: Boolean) {
    val scope = rememberCoroutineScope()
    var loading by remember { mutableStateOf(true) }
    var saving by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var success by remember { mutableStateOf<String?>(null) }
    var configId by remember { mutableStateOf<String?>(null) }

    var paypalEnabled by remember { mutableStateOf(false) }
    var paypalClientId by remember { mutableStateOf("") }
    var paypalSecret by remember { mutableStateOf("") }
    var paypalMode by remember { mutableStateOf(AdminPaypalMode.SANDBOX) }
    var stripeEnabled by remember { mutableStateOf(false) }
    var stripePublicKey by remember { mutableStateOf("") }
    var stripeSecretKey by remember { mutableStateOf("") }
    var modeMenu by remember { mutableStateOf(false) }

    fun reload() {
        scope.launch {
            loading = true
            error = null
            success = null
            try {
                val cfg = AdminRepo.api.getSettingsPayments()
                configId = cfg.id
                paypalEnabled = cfg.paypalEnabled
                paypalClientId = cfg.paypalClientId.orEmpty()
                paypalSecret = cfg.paypalSecret.orEmpty()
                paypalMode = cfg.paypalMode
                stripeEnabled = cfg.stripeEnabled
                stripePublicKey = cfg.stripePublicKey.orEmpty()
                stripeSecretKey = cfg.stripeSecretKey.orEmpty()
            } catch (e: Exception) {
                error = e.userMessage("No se pudo cargar la configuración de pagos.")
            } finally {
                loading = false
            }
        }
    }

    LaunchedEffect(Unit) { reload() }

    VerticalScrollColumn(
        Modifier
            .fillMaxSize()
            .clip(RoundedCornerShape(16.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(if (compact) 16.dp else 24.dp),
    ) {
        when {
            loading -> Box(Modifier.fillMaxWidth().padding(40.dp), contentAlignment = Alignment.Center) {
                CircularProgressIndicator(color = AdminColors.Primary)
            }
            else -> {
                error?.let {
                    Text(it, color = AdminColors.Error, fontSize = 13.sp, modifier = Modifier.padding(bottom = 10.dp))
                }
                success?.let {
                    Text(it, color = AdminColors.Success, fontSize = 13.sp, modifier = Modifier.padding(bottom = 10.dp))
                }

                Text("PayPal", fontWeight = FontWeight.Bold, fontSize = 16.sp)
                Spacer(Modifier.height(6.dp))
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Checkbox(
                        checked = paypalEnabled,
                        onCheckedChange = { paypalEnabled = it },
                        enabled = !saving,
                        colors = CheckboxDefaults.colors(checkedColor = AdminColors.Primary),
                    )
                    Text("Habilitado", color = AdminColors.Text, fontSize = 14.sp)
                }
                Spacer(Modifier.height(10.dp))
                if (compact) {
                    PaymentField("Client ID", paypalClientId, { paypalClientId = it })
                    Spacer(Modifier.height(10.dp))
                    PaymentField("Secreto", paypalSecret, { paypalSecret = it }, secret = true)
                    Spacer(Modifier.height(10.dp))
                    Text("Modo", color = AdminColors.TextMuted, fontSize = 12.sp)
                    Spacer(Modifier.height(4.dp))
                    Box {
                        OutlinedButton(
                            onClick = { modeMenu = true },
                            modifier = Modifier.fillMaxWidth(),
                            enabled = !saving,
                            shape = RoundedCornerShape(10.dp),
                        ) {
                            Text(paypalMode.labelEs(), color = AdminColors.Text, modifier = Modifier.fillMaxWidth())
                        }
                        DropdownMenu(expanded = modeMenu, onDismissRequest = { modeMenu = false }) {
                            AdminPaypalMode.entries.forEach { m ->
                                DropdownMenuItem(
                                    text = { Text(m.labelEs()) },
                                    onClick = {
                                        paypalMode = m
                                        modeMenu = false
                                    },
                                )
                            }
                        }
                    }
                } else {
                    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(16.dp)) {
                        Column(Modifier.weight(1f)) {
                            PaymentField("Client ID", paypalClientId, { paypalClientId = it })
                        }
                        Column(Modifier.weight(1f)) {
                            PaymentField("Secreto", paypalSecret, { paypalSecret = it }, secret = true)
                        }
                    }
                    Spacer(Modifier.height(12.dp))
                    Text("Modo", color = AdminColors.TextMuted, fontSize = 12.sp)
                    Spacer(Modifier.height(4.dp))
                    Box {
                        OutlinedButton(
                            onClick = { modeMenu = true },
                            modifier = Modifier.fillMaxWidth(),
                            enabled = !saving,
                            shape = RoundedCornerShape(10.dp),
                        ) {
                            Text(paypalMode.labelEs(), color = AdminColors.Text, modifier = Modifier.fillMaxWidth())
                        }
                        DropdownMenu(expanded = modeMenu, onDismissRequest = { modeMenu = false }) {
                            AdminPaypalMode.entries.forEach { m ->
                                DropdownMenuItem(
                                    text = { Text(m.labelEs()) },
                                    onClick = {
                                        paypalMode = m
                                        modeMenu = false
                                    },
                                )
                            }
                        }
                    }
                }

                Spacer(Modifier.height(22.dp))
                Text("Stripe", fontWeight = FontWeight.Bold, fontSize = 16.sp)
                Spacer(Modifier.height(6.dp))
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Checkbox(
                        checked = stripeEnabled,
                        onCheckedChange = { stripeEnabled = it },
                        enabled = !saving,
                        colors = CheckboxDefaults.colors(checkedColor = AdminColors.Primary),
                    )
                    Text("Habilitado", color = AdminColors.Text, fontSize = 14.sp)
                }
                Spacer(Modifier.height(10.dp))
                if (compact) {
                    PaymentField("Clave pública", stripePublicKey, { stripePublicKey = it })
                    Spacer(Modifier.height(10.dp))
                    PaymentField("Clave secreta", stripeSecretKey, { stripeSecretKey = it }, secret = true)
                } else {
                    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(16.dp)) {
                        Column(Modifier.weight(1f)) {
                            PaymentField("Clave pública", stripePublicKey, { stripePublicKey = it })
                        }
                        Column(Modifier.weight(1f)) {
                            PaymentField("Clave secreta", stripeSecretKey, { stripeSecretKey = it }, secret = true)
                        }
                    }
                }

                Spacer(Modifier.height(20.dp))
                Button(
                    onClick = {
                        scope.launch {
                            saving = true
                            error = null
                            success = null
                            try {
                                val saved = AdminRepo.api.upsertSettingsPayments(
                                    AdminSettingsPaymentUpsert(
                                        paypalEnabled = paypalEnabled,
                                        paypalClientId = paypalClientId,
                                        paypalSecret = paypalSecret,
                                        paypalMode = paypalMode,
                                        stripeEnabled = stripeEnabled,
                                        stripePublicKey = stripePublicKey,
                                        stripeSecretKey = stripeSecretKey,
                                    ),
                                )
                                configId = saved.id
                                paypalSecret = saved.paypalSecret.orEmpty()
                                stripeSecretKey = saved.stripeSecretKey.orEmpty()
                                success = "Configuración de pagos guardada."
                            } catch (e: Exception) {
                                error = e.userMessage("No se pudo guardar.")
                            } finally {
                                saving = false
                            }
                        }
                    },
                    enabled = !saving,
                    modifier = if (compact) Modifier.fillMaxWidth().height(44.dp) else Modifier,
                    shape = RoundedCornerShape(10.dp),
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text(if (saving) "…" else "Guardar", fontWeight = FontWeight.SemiBold)
                }
            }
        }
    }
}

@Composable
private fun PaymentField(
    label: String,
    value: String,
    onChange: (String) -> Unit,
    secret: Boolean = false,
) {
    Text(label, color = AdminColors.TextMuted, fontSize = 12.sp)
    Spacer(Modifier.height(4.dp))
    val (visible, toggleVisible) = rememberPasswordVisible()
    OutlinedTextField(
        value = value,
        onValueChange = onChange,
        modifier = Modifier.fillMaxWidth(),
        singleLine = true,
        visualTransformation = if (secret && !visible) PasswordVisualTransformation() else VisualTransformation.None,
        trailingIcon = if (secret) {
            { PasswordVisibilityToggle(visible = visible, onToggle = toggleVisible) }
        } else {
            null
        },
        shape = RoundedCornerShape(10.dp),
        colors = OutlinedTextFieldDefaults.colors(
            focusedBorderColor = AdminColors.Primary,
            unfocusedBorderColor = AdminColors.Outline,
        ),
    )
}
